// Portable integration modules (decision D19, slice 1): the catalogue built from the manifests of
// the vendored `@primage/integration-*` packages plus the coming-soon cards, and the per-organization
// module instances. Slice 1 never activates a module: `activation` is always "not_enabled" and an
// instance can only be draft or disabled. Secrets never appear here (config is secret-free).
import { z } from "zod";

const id = z.uuid();
const timestamp = z.iso.datetime();

/** Manifest `category` (stable id; the label is the host's). Unknown categories map to "outros". */
export const MODULE_CATEGORIES = ["canal", "ia", "pagamento", "automacao", "infra", "dados", "outros"] as const;
export type ModuleCategory = (typeof MODULE_CATEGORIES)[number];

export const MODULE_MATURITIES = ["experimental", "beta", "stable", "deprecated"] as const;
export type ModuleMaturity = (typeof MODULE_MATURITIES)[number];

/** Where an instance applies: whole installation, one account (organization) or one connection. */
export const MODULE_SCOPES = ["instalacao", "conta", "conexao"] as const;
export type ModuleScope = (typeof MODULE_SCOPES)[number];

export const MODULE_INSTANCE_STATES = ["draft", "active", "disabled", "error"] as const;
export type ModuleInstanceState = (typeof MODULE_INSTANCE_STATES)[number];

/** States a PATCH may set. `active` is reached only through the audited activate action (D20). */
export const MODULE_INSTANCE_SETTABLE_STATES = ["draft", "disabled"] as const;

/**
 * Installation-wide egress mode for module instances and `integration_module` connections (D20 item 5):
 * `activated` — real calls for instances an organization admin activated in the app (test must pass first);
 * `allowlist` — additionally require the instance id in MAESTROS_ALLOW_REAL_INTEGRATION_MODULES;
 * `off` — no real calls at all (kill switch).
 */
export const MODULE_EGRESS_MODES = ["activated", "allowlist", "off"] as const;
export type ModuleEgressMode = (typeof MODULE_EGRESS_MODES)[number];

export const MODULE_ID_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
export const MODULE_CARD_ID_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
/** Instance config is secret-free and small (the database enforces the same limit). */
export const MODULE_CONFIG_MAX_BYTES = 16_384;

export interface IntegrationCatalogCard {
  /** Card id, unique in the catalogue (e.g. "whatsapp", "meta-instagram", "openrouter", "google-drive"). */
  id: string;
  /** Module that implements the card; null for a coming-soon card. */
  module_id: string | null;
  module_version: string | null;
  name: string;
  summary: string;
  category: ModuleCategory;
  /** Same-origin API path of the icon (`/v1/integration-modules/<module>/assets/<file>`), or null. */
  icon_url: string | null;
  availability: "available" | "coming_soon";
  maturity: ModuleMaturity | null;
  scope: ModuleScope | null;
  capabilities: string[];
  operations: string[];
  instances: { total: number; active: number };
  /**
   * "enabled": the card can be connected (secret + test) and activated by an admin;
   * "not_enabled": the module has no configuration the host supports yet, or egress is off.
   */
  activation: "not_enabled" | "enabled";
}

export interface IntegrationCatalogModule {
  id: string;
  version: string;
  name: string;
  description: string;
  maturity: ModuleMaturity;
  host_contract: string;
}

export interface IntegrationCatalog {
  cards: IntegrationCatalogCard[];
  modules: IntegrationCatalogModule[];
  source: { repository: string };
}

export interface IntegrationModuleInstance {
  id: string;
  module_id: string;
  card_id: string;
  module_version: string;
  scope: ModuleScope;
  project_id: string | null;
  display_name: string;
  config: Record<string, unknown>;
  state: ModuleInstanceState;
  /** Write-only secret of the instance (e.g. the provider API key): state and masked hint only. */
  secret: { state: "missing" | "active" | "revoked"; masked_hint: string | null; updated_at: string | null };
  last_test_at: string | null;
  last_test_result: "ok" | "failed" | null;
  last_test_summary: string | null;
  activated_at: string | null;
  activated_by: string | null;
  created_at: string;
  updated_at: string;
  version: number;
}

const displayName = z.string().trim().min(1).max(120);
const config = z
  .record(z.string().max(100), z.unknown())
  .refine((v) => new TextEncoder().encode(JSON.stringify(v)).length <= MODULE_CONFIG_MAX_BYTES, { message: `config must be at most ${MODULE_CONFIG_MAX_BYTES} bytes` });

export const createModuleInstanceSchema = z
  .object({
    card_id: z.string().regex(MODULE_CARD_ID_RE),
    display_name: displayName,
    project_id: id.nullable().default(null),
    config: config.default({}),
  })
  .strict();
export type CreateModuleInstanceInput = z.infer<typeof createModuleInstanceSchema>;

export const patchModuleInstanceSchema = z
  .object({
    expected_version: z.number().int().nonnegative(),
    display_name: displayName.optional(),
    config: config.optional(),
    state: z.enum(MODULE_INSTANCE_SETTABLE_STATES).optional(),
  })
  .strict();
export type PatchModuleInstanceInput = z.infer<typeof patchModuleInstanceSchema>;

export const moduleInstanceSchema = z.object({
  id,
  module_id: z.string(),
  card_id: z.string(),
  module_version: z.string(),
  scope: z.enum(MODULE_SCOPES),
  project_id: id.nullable(),
  display_name: z.string(),
  config: z.record(z.string(), z.unknown()),
  state: z.enum(MODULE_INSTANCE_STATES),
  secret: z.object({ state: z.enum(["missing", "active", "revoked"]), masked_hint: z.string().nullable(), updated_at: timestamp.nullable() }),
  last_test_at: timestamp.nullable(),
  last_test_result: z.enum(["ok", "failed"]).nullable(),
  last_test_summary: z.string().nullable(),
  activated_at: timestamp.nullable(),
  activated_by: id.nullable(),
  created_at: timestamp,
  updated_at: timestamp,
  version: z.number().int(),
});

/** Stores/replaces one secret field of an instance (write-only; never returned). */
export const putModuleInstanceSecretSchema = z.object({ field: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/), value: z.string().min(1).max(20_000) }).strict();
export type PutModuleInstanceSecretInput = z.infer<typeof putModuleInstanceSecretSchema>;

/** Activation needs the current version (optimistic concurrency) and a named confirmation text. */
export const activateModuleInstanceSchema = z.object({ expected_version: z.number().int().nonnegative(), confirm: z.literal("ATIVAR") }).strict();
export type ActivateModuleInstanceInput = z.infer<typeof activateModuleInstanceSchema>;
